import moment from "moment"
import { unlocalizeUrl } from "./Localization"

export const blogElementsWithTranslations = [
  'Paragraph',
  'Html',
  'Wysiwyg',
  'Heading',
  'Banner',
  'Dropper',
  'Button',
]

export const blogElementsWithCustomCss = [
  'Paragraph',
  'Html',
  'Wysiwyg',
  'Heading',
  'Hr',
  'File',
  'Banner',
  'Dropper',
  'Container',
  'Button',
]

export const availableElements : { value: string, default?: boolean }[] = [
  { value: 'Banner'},
  { value: 'Blog'},
  { value: 'Button'},
  { value: 'Comments'},
  { value: 'Component'},
  { value: 'Container'},
  { value: 'Dropper'},
  { value: 'File'},
  { value: 'Heading'},
  { value: 'Hr'},
  { value: 'Html' },
  { value: 'Meta' },
  { value: 'Paragraph', default: true},
  { value: 'Toc'},
  { value: 'Wysiwyg' },
]

export const availableLayouts : { name: string, value: string, default?: boolean }[] = [
  { name: 'Default', value: 'default', default: true},
  { name: 'Blog', value: 'blog'},
  { name: 'Full', value: 'full'},
  { name: 'Full Gray', value: 'full-gray'},
  { name: 'Gray', value: 'gray'},
  { name: 'Sidenav', value: 'sidenav'},
  { name: 'Gray on white', value: 'gray-on-white'},
  { name: 'Highlighted', value: 'highlighted'},
]

const tmpCache: { [key: string]: Blog|any } = {}

export const methodRenderBlog = async (slug: string) : Promise<Blog|any> => {

  if (tmpCache[slug]) {
    console.log('Cached return', slug)
    return tmpCache[slug]
  }

  let is_resolved = false

  console.log(slug, '[blog] ws.send casted');
  const blog_ws = ws.send<Blog>('blog.url', {
    url: slug,
    slug: slug,
  }).then((res) => {
    console.log(slug, '[blog] ws.send returned');
    return {
      'api': 'ws',
      'data': res
    }
  }).catch(api.parseError)

  console.log(slug, '[blog] api.get casted');
  const blog_api = api.get('blog/url', {url:slug}).then((res: any) => {
    console.log(slug, '[blog] api.get returned');
    return {
      'api': 'rest',
      'data': res?.data?.data
    }
  }).catch(api.parseError)

  // take promise which returns positive result first
  return await Promise.any([blog_ws, blog_api])
    .then((res: any) => {
      if(!is_resolved && res.api) {
        is_resolved = true
        console.log('Resolved blog '+slug+' via', res.api)

        tmpCache[slug] = res.data

        return res.data
      }
    }).catch((err) => {
      if(!is_resolved) {
        is_resolved = true
        console.error('Error resolving blog', slug, err)
        throw err
      }
    })
    .finally(() => {
      // clear cache after 5 minutes
      setTimeout(() => {
        if(tmpCache[slug]) {
          delete tmpCache[slug]
        }
      }, 5 * 1000)
    })
}

export const methodSaveBlog = (blog: Blog) => {
  return ws.send('blog.update', blog)
    .then((res) => {
      notify('Blog saved')
      return res
    })
    .catch(api.parseError)
}

export const methodDeleteBlog = (id: string) => {
  return ws.send('blog.delete', { id })
    .then((res) => {
      notify({
        text: 'Blog deleted',
        button: 'Undo action',
        buttonaction: () => {
          methodUndeleteBlog(id)
        }
      })
      return res
    })
    .catch(api.parseError)
}

export const methodUndeleteBlog = (id: string) => {
  return ws.send('blog.undelete', { id })
    .then((res) => {
      notify(res)
      return res
    })
    .catch(api.parseError)
}

export const methodShowBlog = (blog: Blog) => {
  return useRouter().push(useLocalePath()('/blog/'+ blog?.slug));
}

export const methodDuplicateBlog = (id: string) => {
  return ws.send('blog.duplicate', { id })
    .then((res) => {
      notify('Blog duplicated')
      return res
    })
    .catch(api.parseError)
}

export const methodImportBlog = (text: string) => {
  return ws.send('blog.import', {import: text})
    .then((res) => {
      notify('Blog imported')
      return res
    })
    .catch(api.parseError)
}

export const methodLoadBlogAuthorDetails = (blog_id: string|any) => {
  return ws.send('blog.author', { id: blog_id })
    .then((res) => {
      return res
    })
    .catch(api.parseError)
}

export const methodAttachFileToBlog = (
  blog_id: string|any,
  file_id: string|any
) => {
  return ws.send('blog.attachfile', { id: blog_id, file_id: file_id })
    .then((res) => {
      notify(res)

      return res
    })
    .catch(api.parseError)
}

export const methodLoadBlogFiles = (blog_id: string) => {
  return ws.send('blog.files', { id: blog_id })
    .then((res) => {
      return res
    })
    .catch(api.parseError)
}

export const methodGenerateBlog = (prompt: string) => {
  return ws.send('blog.generate', {prompt})
    .then((res) => {
      notify(res)
      return res
    })
    .catch(api.parseError)
}

export const methodToggleBlogPublished = (blog: Blog) => {

  if (blog.published_at) {
    blog.published_at = null
  } else {
    blog.published_at = moment().utc().format('YYYY-MM-DDTHH:mm:ss')
  }

  return methodSaveBlog(blog)
}

export const methodToggleBlogListed = (blog: Blog) => {
  if (blog.is_listed) {
    blog.is_listed = false
  } else {
    blog.is_listed = true
  }
  return methodSaveBlog(blog)
}

export const methodToggleBlogInternal = (blog: Blog) => {
  if (blog.is_internal) {
    blog.is_internal = false
  } else {
    blog.is_internal = true
  }
  return methodSaveBlog(blog)
}

export const methodToggleBlogSystemContent = (blog: Blog) => {
  if (blog.is_internal) {
    blog.is_system_content = false
  } else {
    blog.is_system_content = true
  }
  return methodSaveBlog(blog)
}

export const methodLoadBlogList = (page: number = 1, options: null|undefined|object, locale?: null|any) => {
  console.log('[blogload]', locale);

  const payload = {
    page,
    options: options ?? {},
    locale,
  }

  if(ws.is_setup.value){
    return ws.send<Blog[]>('blog.list', payload)
      .then((res) => { return res })
      .catch(api.parseError)
  }else{
    return api.get('blog/list', payload)
      .then((res: any) => { return res?.data?.data ?? [] })
      .catch(api.parseError)
  }

}

export const methodLoadBlogCategories = (options: any = null) => {
  if(ws.is_setup.value){
    return ws.send('blog.listcategories',{ options })
      .then((res) => {
        return res
      })
      .catch(api.parseError)
  } else {
    return api.get('blog/categories', { options })
      .then((res: any) => {
        return res?.data?.data ?? res?.data ?? []
      })
      .catch(api.parseError)
  }
}

export const methodGetSelectableBlogs = () => {
  return ws.send('blog.selectable')
    .then((res) => {
      return res
    })
    .catch(api.parseError)
}

export const methodRelateBlog = (
  blog_id: string,
  related_id: string,
  type: string,
  type_information?: string
) => {
  return ws.send('blog.relate', {
    blog_id,
    related_id,
    type,
    type_information
  })
    .then((res) => {
      return res
    })
    .catch(api.parseError)
}

export const methodLoadBlogDirectories = () => {
  return ws.send('blog.directories')
    .then((res) => {
      return res
    })
    .catch(api.parseError)
}

export const methodDeleteBlogFromUrl = (url: string) => {
  return ws.send('blog.deleteurl', { url })
    .then((res) => {
      notify({
        text: res?.message ?? 'Blog deleted',
        button: 'Undo action',
        buttonaction: () => {
          methodUndeleteBlog(res.id)
        }
      })
      return res
    })
    .catch(api.parseError)
}

export const methodRenameBlogDirectory = (old_url: string, new_url: string) => {
  return ws.send('blog.renamedirectory', { old_url, new_url })
    .then((res) => {
      return res
    })
    .catch(api.parseError)
}

export const methodCreateBlogForUrl = (url: string) => {
  return ws.send('blog.createurl', { url: unlocalizeUrl(url) })
    .then((res) => {
      notify(res?.message ?? res ?? 'Blog created')
      return res
    })
    .catch(api.parseError)
}

